/**
 * Sign-in for the sync layer: email + 6-digit code (Supabase email OTP).
 *
 * Why a code and not just a magic link (ADR 0018): the field tablet may not
 * have the designer's email account on it. A link must be opened in the same
 * browser profile as the app — an iPad home-screen install has SEPARATE
 * storage from Safari, so a link opened in Safari would sign in the wrong
 * context. A 6-digit code read off any screen and typed into the app has no
 * such failure mode. The email carries both; the code is the primary flow.
 *
 * AUTH IS ONLY EVER NEEDED FOR SYNC. Nothing in the app may gate local
 * sketching on a session — offline work with an expired session keeps
 * working and queues; sign-in is what lets the queue drain. Sessions persist
 * indefinitely by default (refresh-token rotation is handled inside
 * supabase-js); any predetermined cap is a Supabase dashboard policy, not
 * client code.
 *
 * This wrapper exists so the rest of the app never touches supabase-js
 * directly (the same boundary rule as business rules): the UI talks to this
 * module, this module talks to the client, and tests inject a fake client.
 */

/**
 * @param {object} client a supabase-js client (or a test fake exposing .auth)
 */
export function createAuth(client) {
  const listeners = new Set();

  /**
   * Ask Supabase to email a one-time code (and link) to this address.
   * Creates the account on first sign-in — designers are provisioned by
   * signing in, not by an admin pre-creating rows; RLS ownership starts from
   * whatever auth.uid() ends up being.
   *
   * @returns {{ok: true} | {ok: false, message: string}}
   */
  async function requestCode(email) {
    const address = normalizeEmail(email);
    if (!address) return { ok: false, message: 'Enter an email address.' };
    const { error } = await client.auth.signInWithOtp({ email: address });
    if (error) return { ok: false, message: friendly(error) };
    return { ok: true };
  }

  /**
   * Exchange the emailed code for a session. Type 'email' covers both the
   * first-ever sign-in and returning users — Supabase treats them uniformly.
   */
  async function verifyCode(email, code) {
    const address = normalizeEmail(email);
    const token = String(code ?? '').replace(/\s+/g, '');
    if (!address || !token) return { ok: false, message: 'Enter the email and the code from it.' };
    const { data, error } = await client.auth.verifyOtp({ email: address, token, type: 'email' });
    if (error) return { ok: false, message: friendly(error) };
    return { ok: true, user: data?.user ?? null };
  }

  /**
   * The current session, from local storage — NO network. Safe to call
   * offline at startup; an expired-but-refreshable session still counts as
   * signed in (supabase-js refreshes it in the background when it can).
   */
  async function currentUser() {
    const { data } = await client.auth.getSession();
    return data?.session?.user ?? null;
  }

  async function signOut() {
    // scope:'local' clears this device only; other devices keep their
    // sessions. Global revocation stays an explicit admin action.
    await client.auth.signOut({ scope: 'local' });
  }

  /** Subscribe to sign-in/out changes; returns an unsubscribe function. */
  function onChange(fn) {
    listeners.add(fn);
    const { data } = client.auth.onAuthStateChange((_event, session) => {
      fn(session?.user ?? null);
    });
    return () => {
      listeners.delete(fn);
      data?.subscription?.unsubscribe?.();
    };
  }

  return { requestCode, verifyCode, currentUser, signOut, onChange };
}

function normalizeEmail(email) {
  const s = String(email ?? '').trim().toLowerCase();
  // Deliberately minimal: real validation is Supabase's job (it sends the
  // email or errors). This only catches "obviously not an address" locally
  // so a typo fails fast offline instead of round-tripping.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) ? s : null;
}

// platform-boundary-exception: auth-specific SDK error-string mapping to
// sign-in copy, not classification into platform/errors.js's retry-policy
// decision — see platform/boundary-exceptions.json for the reviewed reason.
/** Map raw auth errors to something a designer can act on. */
function friendly(error) {
  const msg = String(error?.message ?? 'Something went wrong.');
  if (/rate limit/i.test(msg)) return 'Too many attempts — wait a minute and try again.';
  if (/expired|invalid/i.test(msg)) return 'That code is wrong or expired — request a new one.';
  if (/fetch|network/i.test(msg)) return 'No connection — sign-in needs internet (sketching does not).';
  return msg;
}
